/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import useT, {
    UseTranslationOptions
} from "../../../Basis/Internationalisierung/Hooks/useTranslation";
import i18n from "../../../Basis/Internationalisierung/i18n";
import Steuerart from "../../../Shared/Belege/Queries/Steuerart";
import BezeichnungPosition from "../Commands/BezeichnungPosition";
import KonfigurierbareAnzeigeBereich from "../Commands/KonfigurierbareAnzeigeBereich";
import KonfigurierbareAnzeigeTyp from "../Commands/KonfigurierbareAnzeigeTyp";
import { KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP } from "../Commands/KonfigurierbareListenansichtSpalteTyp";
import { KONFIGURIERBARE_LISTENANSICHT_TYP } from "../Commands/KonfigurierbareListenansichtTyp";
import { SORTIERRICHTUNG } from "../Commands/Sortierrichtung";
import { BlockOption } from "../Queries/LadeKonfigurierbareAnzeigeBlock";
import { Schritt } from "../Queries/Schnelleinstieg";
import { ZahlungsmethodeTypenSammlung } from "../Queries/ZahlungsmethodeTyp";
import { FelderGruppe as BrandingFelderGruppe } from "../Views/SchnelleinstiegView/SchnelleinstiegBranding/types";
import { FelderGruppe as FirmendatenFelderGruppe, FirmendatenName } from "../Views/SchnelleinstiegView/SchnelleinstiegFirmendaten/types";

export const useTranslation = (options?: UseTranslationOptions) => useT("i18nEinstellungen", options);
export const fixedT = i18n.getFixedT(null, "i18nEinstellungen");

const schrittTitelTranslationKeys: { [key in Schritt]: `schnelleinstiegStart.schritt${key}Titel` } = {
    0: "schnelleinstiegStart.schritt0Titel",
    1: "schnelleinstiegStart.schritt1Titel",
    2: "schnelleinstiegStart.schritt2Titel",
    3: "schnelleinstiegStart.schritt3Titel",
    4: "schnelleinstiegStart.schritt4Titel"
};

const schrittTextTranslationKeys: { [key in Schritt]: `schnelleinstiegStart.schritt${key}Text` } = {
    0: "schnelleinstiegStart.schritt0Text",
    1: "schnelleinstiegStart.schritt1Text",
    2: "schnelleinstiegStart.schritt2Text",
    3: "schnelleinstiegStart.schritt3Text",
    4: "schnelleinstiegStart.schritt4Text"
};

const schrittTranslationKeys: { [key in Schritt]: `schnelleinstiegSchritte.schritt${key}` } = {
    0: "schnelleinstiegSchritte.schritt0",
    1: "schnelleinstiegSchritte.schritt1",
    2: "schnelleinstiegSchritte.schritt2",
    3: "schnelleinstiegSchritte.schritt3",
    4: "schnelleinstiegSchritte.schritt4"
};

const firmendatenGruppeTitelTranslationKeys: { [key in FirmendatenFelderGruppe]: `schnelleinstiegFirmendaten.${key}Titel` } = {
    name: "schnelleinstiegFirmendaten.nameTitel",
    anschrift: "schnelleinstiegFirmendaten.anschriftTitel",
    kommunikation: "schnelleinstiegFirmendaten.kommunikationTitel",
    rechnungsadresse: "schnelleinstiegFirmendaten.rechnungsadresseTitel",
};

const firmendatenGruppeTextTranslationKeys: { [key in FirmendatenFelderGruppe]: `schnelleinstiegFirmendaten.${key}Text` } = {
    name: "schnelleinstiegFirmendaten.nameText",
    anschrift: "schnelleinstiegFirmendaten.anschriftText",
    kommunikation: "schnelleinstiegFirmendaten.kommunikationText",
    rechnungsadresse: "schnelleinstiegFirmendaten.rechnungsadresseText",
};

const blockOptionTranslationKeys: { [key in BlockOption["name"]]: `konfigurierbareAnzeigeBlockDetailsView.${key}` } = {
    BemerkungAnzeigen: "konfigurierbareAnzeigeBlockDetailsView.BemerkungAnzeigen",
};

const firmendatenInputTranslationKeys: { [key in FirmendatenName] : `firmendatenInput.${key}` } = {
    eMail: "firmendatenInput.eMail",
    bank: "firmendatenInput.bank",
    bic: "firmendatenInput.bic",
    fax: "firmendatenInput.fax",
    mobil: "firmendatenInput.mobil",
    homepage: "firmendatenInput.homepage",
    iban: "firmendatenInput.iban",
    land: "firmendatenInput.land",
    name1: "firmendatenInput.name1",
    name2: "firmendatenInput.name2",
    name3: "firmendatenInput.name3",
    ort: "firmendatenInput.ort",
    postleitzahl: "firmendatenInput.postleitzahl",
    rechnungsadresseEMail: "firmendatenInput.rechnungsadresseEMail",
    rechnungsadresseFax: "firmendatenInput.rechnungsadresseFax",
    rechnungsadresseMobil: "firmendatenInput.rechnungsadresseMobil",
    rechnungsadresseLand: "firmendatenInput.rechnungsadresseLand",
    rechnungsadresseName1: "firmendatenInput.rechnungsadresseName1",
    rechnungsadresseName2: "firmendatenInput.rechnungsadresseName2",
    rechnungsadresseName3: "firmendatenInput.rechnungsadresseName3",
    rechnungsadresseOrt: "firmendatenInput.rechnungsadresseOrt",
    rechnungsadressePostleitzahl: "firmendatenInput.rechnungsadressePostleitzahl",
    rechnungsadresseRegion: "firmendatenInput.rechnungsadresseRegion",
    rechnungsadresseSteuernummer: "firmendatenInput.rechnungsadresseSteuernummer",
    rechnungsadresseStrasse: "firmendatenInput.rechnungsadresseStrasse",
    rechnungsadresseTelefon: "firmendatenInput.rechnungsadresseTelefon",
    rechnungsadresseUmsatzsteuerIdNr: "firmendatenInput.rechnungsadresseUmsatzsteuerIdNr",
    region: "firmendatenInput.region",
    schwerpunktSonstiges: "firmendatenInput.schwerpunktSonstiges",
    steuernummer: "firmendatenInput.steuernummer",
    strasse: "firmendatenInput.strasse",
    telefon: "firmendatenInput.telefon",
    umsatzsteuerIdNr: "firmendatenInput.umsatzsteuerIdNr",
    zusatzangabe1: "firmendatenInput.zusatzangabe1",
    zusatzangabe2: "firmendatenInput.zusatzangabe2",
    zusatzangabe3: "firmendatenInput.zusatzangabe3",
};

const steuerartCaptionTranslationKeys: { [key in Exclude<Steuerart, ""> as `${Lowercase<key>}Caption`]: `schnelleinstiegFinanzeinstellungen.${Lowercase<key>}Caption` } = {
    bruttoCaption: "schnelleinstiegFinanzeinstellungen.bruttoCaption",
    nettoCaption: "schnelleinstiegFinanzeinstellungen.nettoCaption",
    umsatzsteuerbefreitCaption: "schnelleinstiegFinanzeinstellungen.umsatzsteuerbefreitCaption",
};

const steuerartTooltipTranslationKeys: { [key in Exclude<Steuerart, ""> as `${Lowercase<key>}Tootlip`]: `schnelleinstiegFinanzeinstellungen.${Lowercase<key>}Tooltip` } = {
    bruttoTootlip: "schnelleinstiegFinanzeinstellungen.bruttoTooltip",
    nettoTootlip: "schnelleinstiegFinanzeinstellungen.nettoTooltip",
    umsatzsteuerbefreitTootlip: "schnelleinstiegFinanzeinstellungen.umsatzsteuerbefreitTooltip",
};

const brandingGruppeTitelTranslationKeys: { [key in BrandingFelderGruppe]: `schnelleinstiegBranding.${key}Titel` } = {
    schriftart: "schnelleinstiegBranding.schriftartTitel",
    farben: "schnelleinstiegBranding.farbenTitel",
    logos: "schnelleinstiegBranding.logosTitel",
    banner: "schnelleinstiegBranding.bannerTitel",
    seitenfuss: "schnelleinstiegBranding.seitenfussTitel",
    titel: "schnelleinstiegBranding.titelTitel",
};

const brandingGruppeTextTranslationKeys: { [key in BrandingFelderGruppe]: `schnelleinstiegBranding.${key}Text` } = {
    schriftart: "schnelleinstiegBranding.schriftartText",
    farben: "schnelleinstiegBranding.farbenText",
    logos: "schnelleinstiegBranding.logosText",
    banner: "schnelleinstiegBranding.bannerText",
    seitenfuss: "schnelleinstiegBranding.seitenfussText",
    titel: "schnelleinstiegBranding.titelText",
};

const konfigurierbareAnzeigeBereichTranlsationKeys: { [key in KonfigurierbareAnzeigeBereich]: `konfigurierbareAnzeigeBereich.${key}` } = {
    LINKS: "konfigurierbareAnzeigeBereich.LINKS",
    MITTE: "konfigurierbareAnzeigeBereich.MITTE",
    OBEN: "konfigurierbareAnzeigeBereich.OBEN",
    RECHTS: "konfigurierbareAnzeigeBereich.RECHTS",
    UNTEN: "konfigurierbareAnzeigeBereich.UNTEN",
    TITEL: "konfigurierbareAnzeigeBereich.TITEL",
    BUCHEN: "konfigurierbareAnzeigeBereich.BUCHEN",
};

const bezeichnungPositionTranslationKeys: { [key in BezeichnungPosition]: `bezeichnungPosition.${key}` } = {
    UEBERBLOCK: "bezeichnungPosition.UEBERBLOCK",
    VORBLOCK: "bezeichnungPosition.VORBLOCK",
};

export const translationKeys = {
    schnelleinstiegView: {
        weiter: "schnelleinstiegView.weiter",
    },
    schnelleinstiegStart: {
        titel: "schnelleinstiegStart.titel",
        text: "schnelleinstiegStart.text",
        schritt: {
            titel: schrittTitelTranslationKeys,
            text: schrittTextTranslationKeys,
        },
        losGehts: "schnelleinstiegStart.losGehts",
        ueberspringen: "schnelleinstiegStart.ueberspringen",
    },
    schnelleinstiegSchritte: {
        schritt: schrittTranslationKeys,
    },
    schnelleinstiegFirmendaten: {
        titel: firmendatenGruppeTitelTranslationKeys,
        text: firmendatenGruppeTextTranslationKeys,
        weiter: "schnelleinstiegFirmendaten.weiter",
        zurueck: "schnelleinstiegFirmendaten.zurueck",
        hatRechnungsadresse: "schnelleinstiegFirmendaten.hatRechnungsadresse",
    },
    firmendatenInput: {
        ...firmendatenInputTranslationKeys,
    },
    schnelleinstiegFinanzeinstellungen: {
        ...steuerartCaptionTranslationKeys,
        ...steuerartTooltipTranslationKeys,
        titel: "schnelleinstiegFinanzeinstellungen.titel",
        text: "schnelleinstiegFinanzeinstellungen.text",
    },
    schnelleinstiegBranding: {
        titel: brandingGruppeTitelTranslationKeys,
        text: brandingGruppeTextTranslationKeys,
        schriftart: "schnelleinstiegBranding.schriftart",
        sekundaerfarbe: "schnelleinstiegBranding.sekundaerfarbe",
        schriftfarbe: "schnelleinstiegBranding.schriftfarbe",
        primaerfarbe: "schnelleinstiegBranding.primaerfarbe",
        sekundaerfarbeTooltip: "schnelleinstiegBranding.sekundaerfarbeTooltip",
        schriftfarbeTooltip: "schnelleinstiegBranding.schriftfarbeTooltip",
        primaerfarbeTooltip: "schnelleinstiegBranding.primaerfarbeTooltip",
        logoAufHintergrund: "schnelleinstiegBranding.logoAufHintergrund",
        logo: "schnelleinstiegBranding.logo",
        logoTooltip: "schnelleinstiegBranding.logoTooltip",
        agbUrl: "schnelleinstiegBranding.agbUrl",
        datenschutzUrl: "schnelleinstiegBranding.datenschutzUrl",
        impressumUrl: "schnelleinstiegBranding.impressumUrl",
        fusstext: "schnelleinstiegBranding.fusstext",
        banner: "schnelleinstiegBranding.banner",
        bannerTooltip: "schnelleinstiegBranding.bannerTooltip",
        bannerNurAufSucheAnzeigen: "schnelleinstiegBranding.bannerNurAufSucheAnzeigen",
        favicon: "schnelleinstiegBranding.favicon",
        faviconTooltip: "schnelleinstiegBranding.faviconTooltip",
        homepageTitel: "schnelleinstiegBranding.homepageTitel",
        homepageTitelEN: "schnelleinstiegBranding.homepageTitelEN",
    },
    schnelleinstiegStripe: {
        titel: "schnelleinstiegStripe.titel",
        text: "schnelleinstiegStripe.text",
        stripeVerknuepfen: "schnelleinstiegStripe.stripeVerknuepfen",
    },
    schnelleinstiegHeader: {
        schliessen: "schnelleinstiegHeader.schliessen",
    },
    schnelleinstiegZahlungsmethode: {
        titel: "schnelleinstiegZahlungsmethode.titel",
        text_jetzt: "schnelleinstiegZahlungsmethode.text_jetzt",
        text: "schnelleinstiegZahlungsmethode.text",
    },
    zahlungsmethodenView: {
        status: "zahlungsmethodenView.status",
        zahlungsmethode: "zahlungsmethodenView.zahlungsmethode",
        verwendenFuerDirekteZahlung: "zahlungsmethodenView.verwendenFuerDirekteZahlung",
        verwendenFuerSpaetereZahlung: "zahlungsmethodenView.verwendenFuerSpaetereZahlung",
        aktiv: "zahlungsmethodenView.aktiv",
        nichtAktiv: "zahlungsmethodenView.nichtAktiv",
        payPalNichtVerknuepft: "zahlungsmethodenView.payPalNichtVerknuepft",
        stripeNichtVerknuepft: "zahlungsmethodenView.stripeNichtVerknuepft",
        aktivierungAusstehend: "zahlungsmethodenView.aktivierungAusstehend",
        nichtAngefordert: "zahlungsmethodenView.nichtAngefordert",
        speichern: "zahlungsmethodenView.speichern",
        verwendenFuerDirekteZahlungTooltip: "zahlungsmethodenView.verwendenFuerDirekteZahlungTooltip",
        verwendenFuerSpaetereZahlungTooltip: "zahlungsmethodenView.verwendenFuerSpaetereZahlungTooltip",
    },
    zahlungsmethoden: {
        karte: "zahlungsmethoden.karte",
        giroPay: "zahlungsmethoden.giroPay",
        sepa: "zahlungsmethoden.sepa",
        aufRechnung: "zahlungsmethoden.aufRechnung",
        payPal: "zahlungsmethoden.payPal",
    },
    zahlungsdienstleisterView: {
        zahlungsdienstleister: "zahlungsdienstleisterView.zahlungsdienstleister",
        stripe: "zahlungsdienstleisterView.stripe",
        payPal: "zahlungsdienstleisterView.payPal",
        zahlungsmethoden: "zahlungsdienstleisterView.zahlungsmethoden",
    },
    zahlungsmethodenBreadcrumb: {
        zahlungsmethoden: "zahlungsmethodenBreadcrumb.zahlungsmethoden",
    },
    anredenView: {
        speichern: "anredenView.speichern",
        anlegen: "anredenView.anlegen",
        loeschen: "anredenView.loeschen",
        bezeichnungDE: "legeAnredeAnDialog.bezeichnungDE",
        bezeichnungEN: "legeAnredeAnDialog.bezeichnungEN",
    },
    anredenBreadcrumb: {
        anreden: "anredenBreadcrumb.anreden",
    },
    legeAnredeAnDialog: {
        caption: "legeAnredeAnDialog.caption",
        text: "legeAnredeAnDialog.text",
        anlegen: "legeAnredeAnDialog.anlegen",
        abbrechen: "legeAnredeAnDialog.abbrechen",
        bezeichnungDE: "legeAnredeAnDialog.bezeichnungDE",
        bezeichnungEN: "legeAnredeAnDialog.bezeichnungEN",
    },
    loescheAnredenDialog: {
        caption: "loescheAnredenDialog.caption",
        text: "loescheAnredenDialog.text",
    },
    anredenNavigationView: {
        definitionen: "anredenNavigationView.definitionen",
    },
    anredeView: {
        speichern: "anredeView.speichern",
        loeschen: "anredeView.loeschen",
        ueberschrift: "anredeView.ueberschrift",
        bezeichnungDE: "anredeView.bezeichnungDE",
        bezeichnungEN: "anredeView.bezeichnungEN",
    },
    briefanredeEinstellungenView: {
        speichern: "briefanredeEinstellungenView.speichern",
        begruessungPersonalisieren: "briefanredeEinstellungenView.begruessungPersonalisieren",
        begruessung: "briefanredeEinstellungenView.begruessung",
        standardBegruessung: "briefanredeEinstellungenView.standardBegruessung",
        standardBegruessungEn: "briefanredeEinstellungenView.standardBegruessungEn",
        welcheTeilnahmeDaten: "briefanredeEinstellungenView.welcheTeilnahmeDaten",
        anredeVerwenden: "briefanredeEinstellungenView.anredeVerwenden",
        titelVerwenden: "briefanredeEinstellungenView.titelVerwenden",
        vornameVerwenden: "briefanredeEinstellungenView.vornameVerwenden",
        nachnameVerwenden: "briefanredeEinstellungenView.nachnameVerwenden",
        vorschau: "briefanredeEinstellungenView.vorschau",
        anrede: "briefanredeEinstellungenView.anrede",
        titel: "briefanredeEinstellungenView.titel",
        vorname: "briefanredeEinstellungenView.vorname",
        nachname: "briefanredeEinstellungenView.nachname",
        briefanrede: "briefanredeEinstellungenView.briefanrede",
    },
    briefanredeEinstellungenBreadcrumb: {
        briefanrede: "briefanredeEinstellungenBreadcrumb.briefanrede",
    },
    konfigurierbareAnzeigenBreadrcumb: {
        konfigurierbareAnzeigen: "konfigurierbareAnzeigenBreadrcumb.konfigurierbareAnzeigen",
    },
    konfigurierbareAnzeigeBloeckeBreadrcumb: {
        bloecke: "konfigurierbareAnzeigeBloeckeBreadrcumb.bloecke",
    },
    adresseZusatzfeldDefinitionenNavigationView: {
        zusatzfelder: "adresseZusatzfeldDefinitionenNavigationView.zusatzfelder"
    },
    teilnahmeZusatzfeldDefinitionenNavigationView: {
        zusatzfelder: "teilnahmeZusatzfeldDefinitionenNavigationView.zusatzfelder"
    },
    teilnahmeEinstellungenZusatzfelderBreadcrumb: {
        zusatzfelder: "teilnahmeEinstellungenZusatzfelderBreadcrumb.zusatzfelder",
    },
    adresseZusatzfeldDefinitionView: {
        zusatzfeldZuAdressen: "adresseZusatzfeldDefinitionView.zusatzfeldZuAdressen",
    },
    teilnahmeZusatzfeldDefinitionView: {
        zusatzfeldZuTeilnahmen: "teilnahmeZusatzfeldDefinitionView.zusatzfeldZuTeilnahmen",
    },
    zusatzfeldDefinitionenTabsView: {
        veranstaltungen: "zusatzfeldDefinitionenTabsView.veranstaltungen",
        adressen: "zusatzfeldDefinitionenTabsView.adressen",
        teilnahmen: "zusatzfeldDefinitionenTabsView.teilnahmen",
        ressourcenkategorien: "zusatzfeldDefinitionenTabsView.ressourcenkategorien",
        zusatzfelder: "zusatzfeldDefinitionenTabsView.zusatzfelder",
        veranstaltungsreihen: "zusatzfeldDefinitionenTabsView.veranstaltungsreihen",
        dozierende: "zusatzfeldDefinitionenTabsView.dozierende"
    },
    adresseZusatzfeldDefinitionenBreadcrumb: {
        adressen: "adresseZusatzfeldDefinitionenBreadcrumb.adressen",
    },
    teilnahmeZusatzfeldDefinitionBreadcrumb: {
        teilnahmen: "teilnahmeZusatzfeldDefinitionenBreadcrumb.teilnahmen",
    },
    seminarZusatzfeldDefinitionenNavigationView: {
        zusatzfelder: "seminarZusatzfeldDefinitionenNavigationView.zusatzfelder",
    },
    seminarZusatzfeldDefinitionView: {
        zusatzfeldZuVeranstaltungen: "seminarZusatzfeldDefinitionView.zusatzfeldZuVeranstaltungen",
    },
    seminarEinstellungenZusatzfelderBreadcrumb: {
        zusatzfelder: "seminarEinstellungenZusatzfelderBreadcrumb.zusatzfelder",
    },
    seminarZusatzfeldDefinitionBreadcrumb: {
        veranstaltungen: "seminarZusatzfeldDefinitionBreadcrumb.veranstaltungen",
    },
    ressourcenkategorieZusatzfeldDefinitionBreadcrumb: {
        ressourcenkategorien: "ressourcenkategorieZusatzfeldDefinitionBreadcrumb.ressourcenkategorien",
    },
    seminarreiheZusatzfeldDefinitionBreadcrumb: {
        seminarreihen: "seminarreiheZusatzfeldDefinitionBreadcrumb.seminarreihen",
    },
    dozierendZusatzfeldDefinitionBreadcrumb: {
        dozierende: "dozierendZusatzfeldDefinitionBreadcrumb.dozierende",
    },
    dozierendZusatzfeldDefinitionenNavigationView: {
        zusatzfelder: "dozierendZusatzfeldDefinitionenNavigationView.zusatzfelder",
    },
    dozierendZusatzfeldDefinitionView: {
        zusatzfeldZuDozierenden: "dozierendZusatzfeldDefinitionView.zusatzfeldZuDozierenden",
    },
    ressourcenkategorieZusatzfeldDefinitionView: {
        zusatzfeldZuRessourcenkategorien: "ressourcenkategorieZusatzfeldDefinitionView.zusatzfeldZuRessourcenkategorien",
    },
    ressourcenkategorieZusatzfeldDefinitionenNavigationView: {
        zusatzfelder: "ressourcenkategorieZusatzfeldDefinitionenNavigationView.zusatzfelder",
    },
    kategorienView: {
        anlegen: "kategorienView.anlegen",
        loeschen: "kategorienView.loeschen",
        umordnen: "kategorienView.umordnen",
        intern: "kategorienView.intern",
    },
    legeKategorieAnDialog: {
        abortButtonCaption: "legeKategorieAnDialog.abortButtonCaption",
        dialogText: "legeKategorieAnDialog.dialogText",
        dialogCaption: "legeKategorieAnDialog.dialogCaption",
        submitButtonCaption: "legeKategorieAnDialog.submitButtonCaption",
        textInputCaption: "legeKategorieAnDialog.textInputCaption",
        textInputCaptionDE: "legeKategorieAnDialog.textInputCaptionDE",
        textInputCaptionEN: "legeKategorieAnDialog.textInputCaptionEN",
        istIntern: "legeKategorieAnDialog.istIntern",
    },
    loescheKategorienDialog: {
        dialogCaption: "loescheKategorienDialog.dialogCaption",
        dialogText: "loescheKategorienDialog.dialogText",
    },
    kategorieDetailsView: {
        speichern: "kategorieDetailsView.speichern",
        loeschen: "kategorieDetailsView.loeschen",
        bezeichnung: "kategorieDetailsView.bezeichnung",
        istIntern: "kategorieDetailsView.istIntern",
    },
    kategorieView: {
        details: "kategorieView.details",
    },
    konfigurierbareAnzeigeTyp: {
        artikeldetails: "konfigurierbareAnzeigeTyp.artikeldetails",
        artikelsuchergebnis: "konfigurierbareAnzeigeTyp.artikelsuchergebnis",
        dozierenddetails: "konfigurierbareAnzeigeTyp.dozierenddetails",
        dozierendsuchergebnis: "konfigurierbareAnzeigeTyp.dozierendsuchergebnis",
        seminardetails: "konfigurierbareAnzeigeTyp.seminardetails",
        seminarreihedetails: "konfigurierbareAnzeigeTyp.seminarreihedetails",
        seminarsuchergebnis: "konfigurierbareAnzeigeTyp.seminarsuchergebnis",
        seminarreihesuchergebnis: "konfigurierbareAnzeigeTyp.seminarreihesuchergebnis",
        ressourcenkategoriesuchergebnis: "konfigurierbareAnzeigeTyp.ressourcenkategoriesuchergebnis",
        ressourcenkategoriedetails: "konfigurierbareAnzeigeTyp.ressourcenkategoriedetails",
        seminaranmeldung: "konfigurierbareAnzeigeTyp.seminaranmeldung",
        seminarreiheanmeldung: "konfigurierbareAnzeigeTyp.seminarreiheanmeldung"
    } as { [Typ in KonfigurierbareAnzeigeTyp as `${Lowercase<Typ>}`]: `konfigurierbareAnzeigeTyp.${Lowercase<Typ>}` },
    konfigurierbareAnzeigeView: {
        details: "konfigurierbareAnzeigeView.details",
        bloecke: "konfigurierbareAnzeigeView.bloecke",
        konfigurierbareAnzeige: "konfigurierbareAnzeigeView.konfigurierbareAnzeige",
    },
    konfigurierbareAnzeigenView: {
        anlegenCaption: "konfigurierbareAnzeigenView.anlegenCaption",
        loeschenCaption: "konfigurierbareAnzeigenView.loeschenCaption",
        standardCaption: "konfigurierbareAnzeigenView.standardCaption",
        bezeichnung: "konfigurierbareAnzeigenView.bezeichnung",
        standard: "konfigurierbareAnzeigenView.standard",
        dialogTitle: "konfigurierbareAnzeigenView.dialogTitle",
        dialogContentText: "konfigurierbareAnzeigenView.dialogContentText",
        abortCaption: "konfigurierbareAnzeigenView.abortCaption",
        setzeStandardCaption: "konfigurierbareAnzeigenView.setzeStandardCaption",
    },
    loescheKonfigurierbareAnzeigenDialog: {
        dialogCaption: "loescheKonfigurierbareAnzeigenDialog.dialogCaption",
        dialogText: "loescheKonfigurierbareAnzeigenDialog.dialogText",
    },
    konfigurierbareAnzeigeDetailsView: {
        speichern: "konfigurierbareAnzeigeDetailsView.speichern",
        loeschen: "konfigurierbareAnzeigeDetailsView.loeschen",
        details: "konfigurierbareAnzeigeDetailsView.details",
        bezeichnung: "konfigurierbareAnzeigeDetailsView.bezeichnung",
        setzeStandard: "konfigurierbareAnzeigeDetailsView.setzeStandard",
    },
    konfigurierbareAnzeigenNavigationView: {
        konfigurierbareAnzeigen: "konfigurierbareAnzeigenNavigationView.konfigurierbareAnzeigen",
    },
    konfigurierbareAnzeigeBloeckeView: {
        sortierenCaption: "konfigurierbareAnzeigeBloeckeView.sortierenCaption",
        nurAngezeigteCaption: "konfigurierbareAnzeigeBloeckeView.nurAngezeigteCaption",
        block: "konfigurierbareAnzeigeBloeckeView.block",
        bereich: "konfigurierbareAnzeigeBloeckeView.bereich",
        bezeichnung: "konfigurierbareAnzeigeBloeckeView.bezeichnung",
    },
    konfigurierbareAnzeigeBloeckeNavigationView: {
        bloecke: "konfigurierbareAnzeigeBloeckeNavigationView.bloecke",
    },
    konfigurierbareAnzeigeBlockView: {
        details: "konfigurierbareAnzeigeBlockView.details",
        block: "konfigurierbareAnzeigeBlockView.block",
    },
    konfigurierbareAnzeigeBlockDetailsView: {
        nichtAnzeigen: "konfigurierbareAnzeigeBlockDetailsView.nichtAnzeigen",
        bezeichnung: "konfigurierbareAnzeigeBlockDetailsView.bezeichnung",
        bezeichnungEN: "konfigurierbareAnzeigeBlockDetailsView.bezeichnungEN",
        bezeichnungPosition: "konfigurierbareAnzeigeBlockDetailsView.bezeichnungPosition",
        bereich: "konfigurierbareAnzeigeBlockDetailsView.bereich",
        details: "konfigurierbareAnzeigeBlockDetailsView.details",
        speichern: "konfigurierbareAnzeigeBlockDetailsView.speichern",
        ...blockOptionTranslationKeys,
    },
    bezeichnungPosition: bezeichnungPositionTranslationKeys,
    konfigurierbareAnzeigeBereich: konfigurierbareAnzeigeBereichTranlsationKeys,
    seminaranzeigenBreadcrumb: {
        anzeigen: "seminaranzeigenBreadcrumb.anzeigen",
    },
    seminaranzeigenView: {
        speichernCaption: "seminaranzeigenView.speichernCaption",
        veranstaltungssuchergebnis: "seminaranzeigenView.veranstaltungssuchergebnis",
        veranstaltungsdetails: "seminaranzeigenView.veranstaltungsdetails",
        veranstaltungsanmeldung: "seminaranzeigenView.veranstaltungsanmeldung",
    },
    seminarreiheanzeigenBreadcrumb: {
        anzeigen: "seminarreiheanzeigenBreadcrumb.anzeigen",
    },
    seminarreiheanzeigenView: {
        speichernCaption: "seminarreiheanzeigenView.speichernCaption",
        veranstaltungsreihesuchergebnis: "seminarreiheanzeigenView.veranstaltungsreihesuchergebnis",
        veranstaltungsreihedetails: "seminarreiheanzeigenView.veranstaltungsreihedetails",
        veranstaltungsreiheanmeldung: "seminarreiheanzeigenView.veranstaltungsreiheanmeldung"
    },
    dozierendanzeigenBreadcrumb: {
        anzeigen: "dozierendanzeigenBreadcrumb.anzeigen",
    },
    dozierendanzeigenView: {
        speichernCaption: "dozierendanzeigenView.speichernCaption",
        dozierendsuchergebnis: "dozierendanzeigenView.dozierendsuchergebnis",
        dozierenddetails: "dozierendanzeigenView.dozierenddetails",
    },
    konfigurierbareAnzeigeBereichChip: {
        nichtAnzeigen: "konfigurierbareAnzeigeBereichChip.nichtAnzeigen",
    },
    artikelanzeigenBreadcrumb: {
        anzeigen: "artikelanzeigenBreadcrumb.anzeigen",
    },
    artikelanzeigenView: {
        speichernCaption: "artikelanzeigenView.speichernCaption",
        artikelsuchergebnis: "artikelanzeigenView.artikelsuchergebnis",
        artikeldetails: "artikelanzeigenView.artikeldetails",
    },
    ressourcenkategorieanzeigenBreadcrumb: {
        anzeigen: "ressourcenkategorieanzeigenBreadcrumb.anzeigen",
    },
    ressourcenkategorieanzeigenView: {
        speichernCaption: "ressourcenkategorieanzeigenView.speichernCaption",
        ressourcenkategoriesuchergebnis: "ressourcenkategorieanzeigenView.ressourcenkategoriesuchergebnis",
        ressourcenkategoriedetails: "ressourcenkategorieanzeigenView.ressourcenkategoriedetails",
    },
    preisstammSelect: {
        preis: "preisstammSelect.preis"
    },
    legePreisAnDialog: {
        caption: "legePreisAnDialog.caption",
        text: "legePreisAnDialog.text",
        anlegen: "legePreisAnDialog.anlegen",
        abbrechen: "legePreisAnDialog.abbrechen",
        bezeichnung: "legePreisAnDialog.bezeichnung",
        betrag: "legePreisAnDialog.betrag",
        steuersatz: "legePreisAnDialog.steuersatz"
    },
    legePreispositionAnDialog: {
        bezeichnung: "legePreispositionAnDialog.bezeichnung",
        bezeichnungPlatzhalter: "legePreispositionAnDialog.bezeichnungPlatzhalter",
        betrag: "legePreispositionAnDialog.betrag",
        steuersatz: "legePreispositionAnDialog.steuersatz",
        dialogCaption: "legePreispositionAnDialog.dialogCaption",
        dialogText: "legePreispositionAnDialog.dialogText",
        submitButtonCaption: "legePreispositionAnDialog.submitButtonCaption",
        abortButtonCaption: "legePreispositionAnDialog.abortButtonCaption",
    },
    loeschePreiseDialog: {
        caption: "loeschePreiseDialog.caption",
        text: "loeschePreiseDialog.text",
    },
    loeschePreispositionenDialog: {
        caption: "loeschePreispositionenDialog.dialogCaption",
        text: "loeschePreispositionenDialog.dialogText",
    },
    preiseView: {
        bezeichnung: "preiseView.bezeichnung",
        betrag: "preiseView.betrag",
        intern: "preiseView.intern",
        keineBezeichnung: "preiseView.keineBezeichnung",
        anlegen: "preiseView.anlegen",
        loeschen: "preiseView.loeschen",
        preise: "preiseView.preise"
    },
    preiseNavigationView: {
        titel: "preiseNavigationView.titel",
        keineBezeichnung: "preiseNavigationView.keineBezeichnung"
    },
    preisView: {
        preis: "preisView.preis",
        details: "preisView.details",
        positionen: "preisView.positionen",
        keineBezeichnung: "preisView.keineBezeichnung"
    },
    preisDetailsView: {
        speichern: "preisDetailsView.speichern",
        loeschen: "preisDetailsView.loeschen",
        bezeichnung: "preisDetailsView.bezeichnung",
        betrag: "preisDetailsView.betrag",
        istIntern: "preisDetailsView.istIntern",
        beginn: "preisDetailsView.beginn",
        ende: "preisDetailsView.ende",
        steuersatz: "preisDetailsView.steuersatz",
        bezeichnungPlatzhalter: "preisDetailsView.bezeichnungPlatzhalter",
        mehrerePreispositionen: "preisDetailsView.mehrerePreispositionen",
        istInternTooltip: "preisDetailsView.istInternTooltip",
        sachkontoTooltip: "preisDetailsView.sachkontoTooltip",
        kontingent: "preisDetailsView.kontingent",
        details: "preisDetailsView.details",
        positionen: "preisDetailsView.positionen",
        preis: "preisDetailsView.preis",
        steuer: "preisDetailsView.steuer",
        von: "preisDetailsView.von",
        sind: "preisDetailsView.sind",
        gesamtbetragNetto: "preisDetailsView.gesamtbetragNetto",
        gesamtbetragBrutto: "preisDetailsView.gesamtbetragBrutto"
    },
    preispositionenView: {
        anlegen: "preispositionenView.anlegen",
        loeschen: "preispositionenView.loeschen",
        bezeichnung: "preispositionenView.bezeichnung",
        betrag: "preispositionenView.betrag",
        steuersatz: "preispositionenView.steuersatz",
    },
    preispositionView: {
        details: "preispositionView.details",
        keineBezeichnung: "preispositionView.keineBezeichnung"
    },
    preispositionDetailsView: {
        bezeichnungPlatzhalter: "preispositionDetailsView.bezeichnungPlatzhalter",
        bezeichnung: "preispositionDetailsView.bezeichnung",
        betrag: "preispositionDetailsView.betrag",
        steuersatz: "preispositionDetailsView.steuersatz",
        speichern: "preispositionDetailsView.speichern",
        loeschen: "preispositionDetailsView.loeschen",
        sachkontoTooltip: "preispositionDetailsView.sachkontoTooltip"
    },
    preispositionenNavigationView: {
        keineBezeichnung: "preispositionenNavigationView.keineBezeichnung",
        titel: "preispositionenNavigationView.titel"
    },
    preisBreadcrumb: {
        keineBezeichnung: "preispositionenNavigationView.keineBezeichnung",
    },
    preispositionBreadcrumb: {
        keineBezeichnung: "preispositionBreadcrumb.keineBezeichnung",
    },
    konfigurierbareListenansichtenView: {
        listenansicht: "konfigurierbareListenansichtenView.listenansicht",
        listenansichten: "konfigurierbareListenansichtenView.listenansichten",
    },
    konfigurierbareListenansichtView: {
        spaltenReihenfolge: "konfigurierbareListenansichtView.spaltenReihenfolge",
        listenansicht: "konfigurierbareListenansichtView.listenansicht",
        sortierung: "konfigurierbareListenansichtView.sortierung",
        filter: "konfigurierbareListenansichtView.filter",
    },
    konfigurierbareListenansichtFilterView: {
        speichern: "konfigurierbareListenansichtFilterView.speichern",
    },
    konfigurierbareListenansichtenNavigationView: {
        listenansichten: "konfigurierbareListenansichtenNavigationView.listenansichten",
    },
    konfigurierbareListenansichtAnpassenDialog: {
        fertig: "konfigurierbareListenansichtAnpassenDialog.fertig",
        aufStandardZuruechsetzen: "konfigurierbareListenansichtAnpassenDialog.aufStandardZuruecksetzen",
        dialogTitel: "konfigurierbareListenansichtAnpassenDialog.dialogTitel",
        abbrechen: "konfigurierbareListenansichtAnpassenDialog.abbrechen",
        spalten: "konfigurierbareListenansichtAnpassenDialog.spalten",
        sortierung: "konfigurierbareListenansichtAnpassenDialog.sortierung",
        filter: "konfigurierbareListenansichtAnpassenDialog.filter",
    },
    konfigurierbareListenansichtAnpassenCommandBarButton: {
        anpassen: "konfigurierbareListenansichtAnpassenCommandBarButton.anpassen",
    },
    konfigurierbareListenansichtSpaltenDragAndDropTable: {
        spalte: "konfigurierbareListenansichtSpaltenDragAndDropTable.spalte",
        anzeigen: "konfigurierbareListenansichtSpaltenDragAndDropTable.anzeigen",
    },
    konfigurierbareListenansichtenBreadcrumb: {
        konfigurierbareListenansichten: "konfigurierbareListenansichtenBreadcrumb.listenansichten",
    },
    konfigurierbareListenansichtSortierungenDragAndDropTable: {
        sortierenNach: "konfigurierbareListenansichtSortierungenDragAndDropTable.sortierenNach",
        verwenden: "konfigurierbareListenansichtSortierungenDragAndDropTable.verwenden",
        absteigendAufsteigend: "konfigurierbareListenansichtSortierungenDragAndDropTable.absteigendAufsteigend",
    },
    konfigurierbareListenansichtSortierungBreadcrumb: {
        sortierung: "konfigurierbareListenansichtSortierungBreadcrumb.sortierung",
    },
    eMailTexteView: {
        eMailTexte: "eMailTexteView.eMailTexte",
    },
    eMailTexteBreadcrumb: {
        eMailTexte: "eMailTexteBreadcrumb.eMailTexte",
    },
    eMailTextNavigationView: {
        eMailTexte: "eMailTextNavigationView.eMailTexte"
    },
    eMailTextView: {
        eMailText: "eMailTextView.eMailText",
        speichernCaption: "eMailTextView.speichernCaption",
        zuruecksetzen: "eMailTextView.zuruecksetzen",
        template: "eMailTextView.template",
        vorschau: "eMailTextView.vorschau",
        vorlageEN: "eMailTextView.vorlageEN",
        vorlageDE: "eMailTextView.vorlageDE",
        templateBetreff: "eMailTextView.templateBetreff",
        betreffEN: "eMailTextView.betreffEN",
        betreffDE: "eMailTextView.betreffDE",
        zuruecksetzenMeldung: "eMailTextView.zuruecksetzenMeldung",
    },
    eMailEinstellungenView: {
        eMailEinstellungen: "eMailEinstellungenView.eMailEinstellungen",
        details: "eMailEinstellungenView.details",
        eMail: "eMailEinstellungenView.eMail"
    },
    eMailEinstellungenDetailsView: {
        speichern: "eMailEinstellungenDetailsView.speichern",
        bestellbestaetigungVersenden: "eMailEinstellungenDetailsView.bestellbestaetigungVersenden"
    },
    eMailEinstellungenBreadcrumb: {
        eMailEinstellungen: "eMailEinstellungenBreadcrumb.eMailEinstellungen",
    },
    useKonfigurierbareListenansicht: {
        auswahlEntfernen: "useKonfigurierbareListenansicht.auswahlEntfernen",
    },
    aushangDetailsView: {
        speichern: "aushangDetailsView.speichern",
        detailsCaption: "aushangDetailsView.detailsCaption",
        bildCaption: "aushangDetailsView.bildCaption",
        titel: {
            de: "aushangDetailsView.titel.de",
            en: "aushangDetailsView.titel.en"
        },
        werbetext: {
            de: "aushangDetailsView.werbetext.de",
            en: "aushangDetailsView.werbetext.en"
        },
        hintergrundbild: "aushangDetailsView.hintergrundbild",
        werbetextTooltip: "aushangDetailsView.werbetextTooltip",
        aushangLink: "aushangDetailsView.aushangLink",
        aushangLinkText: "aushangDetailsView.aushangLinkText"
    },
    aushangRessourcenView: {
        speichern: "aushangRessourcenView.speichern",
        ressourcen: "aushangRessourcenView.ressourcen",
        aufAushang: "aushangRessourcenView.aufAushang",
    },
    digitalerAushangView: {
        veranstaltung: "digitalerAushangView.veranstaltung",
        raum: "digitalerAushangView.raum",
        titel: "digitalerAushangView.titel",
        keineBelegungen: "digitalerAushangView.keineBelegungen"
    },
};

export const zahlungsmethodeTranslationKeyMap = {
    [ZahlungsmethodeTypenSammlung.Karte]: translationKeys.zahlungsmethoden.karte,
    [ZahlungsmethodeTypenSammlung.GiroPay]: translationKeys.zahlungsmethoden.giroPay,
    [ZahlungsmethodeTypenSammlung.Sepa]: translationKeys.zahlungsmethoden.sepa,
    [ZahlungsmethodeTypenSammlung.AufRechnung]: translationKeys.zahlungsmethoden.aufRechnung,
    [ZahlungsmethodeTypenSammlung.PayPal]: translationKeys.zahlungsmethoden.payPal,
}

export const konfigurierbareListenansichtSpalteTranslationKeyMap = {
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.SEMINARE_MATCHCODE]: "konfigurierbareListenansichtenSpalteTyp.seminare.matchcode",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.SEMINARE_STATUS]: "konfigurierbareListenansichtenSpalteTyp.seminare.status",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.SEMINARE_TEILNAHMEN]: "konfigurierbareListenansichtenSpalteTyp.seminare.teilnahmen",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.SEMINARE_BEGINN]: "konfigurierbareListenansichtenSpalteTyp.seminare.beginn",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.SEMINARE_ENDE]: "konfigurierbareListenansichtenSpalteTyp.seminare.ende",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.SEMINARE_TEILNAHMENMANUELLGENEHMIGEN]: "konfigurierbareListenansichtenSpalteTyp.seminare.teilnahmenManuellGenehmigen",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.SEMINARE_TITEL]: "konfigurierbareListenansichtenSpalteTyp.seminare.titel",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.SEMINARE_SEMINARNUMMER]: "konfigurierbareListenansichtenSpalteTyp.seminare.seminarnummer",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.SEMINARE_MAXIMALETEILNAHMEZAHL]: "konfigurierbareListenansichtenSpalteTyp.seminare.maximaleTeilnahmezahl",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.SEMINARE_MINIMALETEILNAHMEZAHL]: "konfigurierbareListenansichtenSpalteTyp.seminare.minimaleTeilnahmezahl",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.SEMINARE_GEBLOCKTETEILNAHMEZAHL]: "konfigurierbareListenansichtenSpalteTyp.seminare.geblockteTeilnahmezahl",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.SEMINARE_AKTUELLETEILNAHMEZAHL]: "konfigurierbareListenansichtenSpalteTyp.seminare.aktuelleTeilnahmezahl",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.SEMINARE_BUCHUNGMOEGLICHAB]: "konfigurierbareListenansichtenSpalteTyp.seminare.buchungMoeglichAb",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.SEMINARE_BUCHUNGMOEGLICHBIS]: "konfigurierbareListenansichtenSpalteTyp.seminare.buchungMoeglichBis",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.SEMINARE_NURBUCHBARBISBEGINN]: "konfigurierbareListenansichtenSpalteTyp.seminare.nurBuchbarBisBeginn",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.SEMINARE_KATEGORIEN]: "konfigurierbareListenansichtenSpalteTyp.seminare.kategorien",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.SEMINARE_SACHKONTO]: "konfigurierbareListenansichtenSpalteTyp.seminare.sachkonto",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.SEMINARE_DOZIERENDEBENACHRICHTIGEN]: "konfigurierbareListenansichtenSpalteTyp.seminare.dozierendeBenachrichtigen",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.SEMINARE_VERHALTENBEIDOPPELTERTEILNAHME]: "konfigurierbareListenansichtenSpalteTyp.seminare.verhaltenBeiDoppelterTeilnahme",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.SEMINARE_PRUEFUNGVONDOPPELTENTEILNAHMEN]: "konfigurierbareListenansichtenSpalteTyp.seminare.pruefungVonDoppeltenTeilnahmen",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.SEMINARE_BENACHRICHTIGEBEIBUCHUNG]: "konfigurierbareListenansichtenSpalteTyp.seminare.benachrichtigeBeiBuchung",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.SEMINARE_BUCHUNGAUFWARTELISTE]: "konfigurierbareListenansichtenSpalteTyp.seminare.buchungAufWarteliste",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.SEMINARE_WARTELISTEAUTOMATISCHNACHRUECKEN]: "konfigurierbareListenansichtenSpalteTyp.seminare.wartelisteAutomatischNachrücken",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.SEMINARE_RECHNUNGENMANUELLFREIGEBEN]: "konfigurierbareListenansichtenSpalteTyp.seminare.rechnungManuellFreigeben",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.SEMINARE_NURINSEMINARREIHEBUCHBAR]: "konfigurierbareListenansichtenSpalteTyp.seminare.nurInSeminarreiheBuchbar",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.SEMINARE_LINKZURONLINEBUCHUNG]: "konfigurierbareListenansichtenSpalteTyp.seminare.linkZurOnlinebuchung",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.SEMINARE_KURZERLINKZURONLINEBUCHUNG]: "konfigurierbareListenansichtenSpalteTyp.seminare.kurzerLinkZurOnlinebuchung",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.SEMINARE_TEILNAHMEERINNERUNGXTAGEVORBEGINN]: "konfigurierbareListenansichtenSpalteTyp.seminare.teilnahmeerinnerungXTageVorBeginn",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.SEMINARE_KOMMENDEAUFSTEIGENDVERGANGENEABSTEIGEND]: "konfigurierbareListenansichtenSpalteTyp.seminare.kommendeAufsteigendVergangeneAbsteigend",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.SEMINARE_ORT]: "konfigurierbareListenansichtenSpalteTyp.seminare.ort",

    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.TEILNAHMEN_BEGINN]: "konfigurierbareListenansichtenSpalteTyp.teilnahmen.beginn",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.TEILNAHMEN_BEMERKUNGEN]: "konfigurierbareListenansichtenSpalteTyp.teilnahmen.bemerkungen",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.TEILNAHMEN_BESTELLNUMMER]: "konfigurierbareListenansichtenSpalteTyp.teilnahmen.bestellnummer",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.TEILNAHMEN_BUCHUNGSDATUM]: "konfigurierbareListenansichtenSpalteTyp.teilnahmen.buchungsdatum",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.TEILNAHMEN_ENDE]: "konfigurierbareListenansichtenSpalteTyp.teilnahmen.ende",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.TEILNAHMEN_ERHAELTZERTIFIKATE]: "konfigurierbareListenansichtenSpalteTyp.teilnahmen.erhaeltZertifikate",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.TEILNAHMEN_HERKUNFT]: "konfigurierbareListenansichtenSpalteTyp.teilnahmen.herkunft",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.TEILNAHMEN_ISTABGESAGT]: "konfigurierbareListenansichtenSpalteTyp.teilnahmen.istAbgesagt",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.TEILNAHMEN_ISTSTORNIERT]: "konfigurierbareListenansichtenSpalteTyp.teilnahmen.istStorniert",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.TEILNAHMEN_MATCHCODE]: "konfigurierbareListenansichtenSpalteTyp.teilnahmen.matchcode",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.TEILNAHMEN_PREIS]: "konfigurierbareListenansichtenSpalteTyp.teilnahmen.preis",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.TEILNAHMEN_SEMINARMATCHCODE]: "konfigurierbareListenansichtenSpalteTyp.teilnahmen.seminarMatchcode",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.TEILNAHMEN_SEMINAREMATCHCODE]: "konfigurierbareListenansichtenSpalteTyp.teilnahmen.seminareMatchcode",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.TEILNAHMEN_STATUS]: "konfigurierbareListenansichtenSpalteTyp.teilnahmen.status",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.TEILNAHMEN_RECHNUNGSADRESSEMATCHCODE]: "konfigurierbareListenansichtenSpalteTyp.teilnahmen.rechnungsadresseMatchcode",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.TEILNAHMEN_TEILNAHMEADRESSEMATCHCODE]: "konfigurierbareListenansichtenSpalteTyp.teilnahmen.teilnahmeadresseMatchcode",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.TEILNAHMEN_RECHNUNGSADRESSEEMAIL]: "konfigurierbareListenansichtenSpalteTyp.teilnahmen.rechnungsadresseEMail",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.TEILNAHMEN_TEILNAHMEADRESSEEMAIL]: "konfigurierbareListenansichtenSpalteTyp.teilnahmen.teilnahmeadresseEMail",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.TEILNAHMEN_ANWESEND]: "konfigurierbareListenansichtenSpalteTyp.teilnahmen.anwesend",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.TEILNAHMEN_TERMINE]: "konfigurierbareListenansichtenSpalteTyp.teilnahmen.termine",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.TEILNAHMEN_ARTIKEL]: "konfigurierbareListenansichtenSpalteTyp.teilnahmen.artikel",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.TEILNAHMEN_ZUSATZFELD]: "konfigurierbareListenansichtenSpalteTyp.teilnahmen.zusatzfeld",

    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.ZAHLUNGEN_BETRAG]: "konfigurierbareListenansichtenSpalteTyp.zahlungen.betrag",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.ZAHLUNGEN_ZAHLUNGSTYP]: "konfigurierbareListenansichtenSpalteTyp.zahlungen.typ",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.ZAHLUNGEN_ZAHLUNGSSTATUS]: "konfigurierbareListenansichtenSpalteTyp.zahlungen.status",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.ZAHLUNGEN_DATUM]: "konfigurierbareListenansichtenSpalteTyp.zahlungen.datum",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.ZAHLUNGEN_BEZEICHNUNG]: "konfigurierbareListenansichtenSpalteTyp.zahlungen.bezeichnung",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.ZAHLUNGEN_BELEGNUMMER]: "konfigurierbareListenansichtenSpalteTyp.zahlungen.belegnummer",

    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.ADRESSEN_MATCHCODE]: "konfigurierbareListenansichtenSpalteTyp.adressen.matchcode",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.ADRESSEN_EMAIL]: "konfigurierbareListenansichtenSpalteTyp.adressen.email",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.ADRESSEN_ADRESSART]: "konfigurierbareListenansichtenSpalteTyp.adressen.adressart",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.ADRESSEN_ANREDE]: "konfigurierbareListenansichtenSpalteTyp.adressen.anrede",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.ADRESSEN_BRIEFANREDE]: "konfigurierbareListenansichtenSpalteTyp.adressen.briefanrede",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.ADRESSEN_FAX]: "konfigurierbareListenansichtenSpalteTyp.adressen.fax",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.ADRESSEN_GEBURTSDATUM]: "konfigurierbareListenansichtenSpalteTyp.adressen.geburtsdatum",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.ADRESSEN_GESCHLECHT]: "konfigurierbareListenansichtenSpalteTyp.adressen.geschlecht",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.ADRESSEN_HATRECHNUNGSADRESSE]: "konfigurierbareListenansichtenSpalteTyp.adressen.hatRechnungsadresse",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.ADRESSEN_HOMEPAGE]: "konfigurierbareListenansichtenSpalteTyp.adressen.homepage",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.ADRESSEN_ISTHAUPTADRESSE]: "konfigurierbareListenansichtenSpalteTyp.adressen.istHauptadresse",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.ADRESSEN_KATEGORIEN]: "konfigurierbareListenansichtenSpalteTyp.adressen.kategorien",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.ADRESSEN_LAND]: "konfigurierbareListenansichtenSpalteTyp.adressen.land",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.ADRESSEN_LANDBEZEICHNUNG]: "konfigurierbareListenansichtenSpalteTyp.adressen.landBezeichnung",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.ADRESSEN_MOBIL]: "konfigurierbareListenansichtenSpalteTyp.adressen.mobil",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.ADRESSEN_NACHNAME]: "konfigurierbareListenansichtenSpalteTyp.adressen.nachname",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.ADRESSEN_NAME1]: "konfigurierbareListenansichtenSpalteTyp.adressen.name1",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.ADRESSEN_NAME2]: "konfigurierbareListenansichtenSpalteTyp.adressen.name2",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.ADRESSEN_NAME3]: "konfigurierbareListenansichtenSpalteTyp.adressen.name3",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.ADRESSEN_ORT]: "konfigurierbareListenansichtenSpalteTyp.adressen.ort",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.ADRESSEN_POSTLEITZAHL]: "konfigurierbareListenansichtenSpalteTyp.adressen.postleitzahl",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.ADRESSEN_REGION]: "konfigurierbareListenansichtenSpalteTyp.adressen.region",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.ADRESSEN_STRASSE]: "konfigurierbareListenansichtenSpalteTyp.adressen.strasse",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.ADRESSEN_TELEFON]: "konfigurierbareListenansichtenSpalteTyp.adressen.telefon",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.ADRESSEN_TITEL]: "konfigurierbareListenansichtenSpalteTyp.adressen.titel",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.ADRESSEN_VORNAME]: "konfigurierbareListenansichtenSpalteTyp.adressen.vorname",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.ADRESSEN_RECHNUNGSADRESSEADRESSART]: "konfigurierbareListenansichtenSpalteTyp.adressen.rechnungsadresseAdressart",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.ADRESSEN_RECHNUNGSADRESSEANREDE]: "konfigurierbareListenansichtenSpalteTyp.adressen.rechnungsadresseAnrede",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.ADRESSEN_RECHNUNGSADRESSEBRIEFANREDE]: "konfigurierbareListenansichtenSpalteTyp.adressen.rechnungsadresseBriefanrede",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.ADRESSEN_RECHNUNGSADRESSEEMAIL]: "konfigurierbareListenansichtenSpalteTyp.adressen.rechnungsadresseEMail",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.ADRESSEN_RECHNUNGSADRESSEFAX]: "konfigurierbareListenansichtenSpalteTyp.adressen.rechnungsadresseFax",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.ADRESSEN_RECHNUNGSADRESSEGEBURTSDATUM]: "konfigurierbareListenansichtenSpalteTyp.adressen.rechnungsadresseGeburtsdatum",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.ADRESSEN_RECHNUNGSADRESSEGESCHLECHT]: "konfigurierbareListenansichtenSpalteTyp.adressen.rechnungsadresseGeschlecht",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.ADRESSEN_RECHNUNGSADRESSEHOMEPAGE]: "konfigurierbareListenansichtenSpalteTyp.adressen.rechnungsadresseHomepage",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.ADRESSEN_RECHNUNGSADRESSELAND]: "konfigurierbareListenansichtenSpalteTyp.adressen.rechnungsadresseLand",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.ADRESSEN_RECHNUNGSADRESSELANDBEZEICHNUNG]: "konfigurierbareListenansichtenSpalteTyp.adressen.rechnungsadresseLandbezeichnung",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.ADRESSEN_RECHNUNGSADRESSEMOBIL]: "konfigurierbareListenansichtenSpalteTyp.adressen.rechnungsadresseMobil",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.ADRESSEN_RECHNUNGSADRESSENACHNAME]: "konfigurierbareListenansichtenSpalteTyp.adressen.rechnungsadresseNachname",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.ADRESSEN_RECHNUNGSADRESSENAME1]: "konfigurierbareListenansichtenSpalteTyp.adressen.rechnungsadresseName1",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.ADRESSEN_RECHNUNGSADRESSENAME2]: "konfigurierbareListenansichtenSpalteTyp.adressen.rechnungsadresseName2",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.ADRESSEN_RECHNUNGSADRESSENAME3]: "konfigurierbareListenansichtenSpalteTyp.adressen.rechnungsadresseName3",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.ADRESSEN_RECHNUNGSADRESSEORT]: "konfigurierbareListenansichtenSpalteTyp.adressen.rechnungsadresseOrt",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.ADRESSEN_RECHNUNGSADRESSEPOSTLEITZAHL]: "konfigurierbareListenansichtenSpalteTyp.adressen.rechnungsadressePostleitzahl",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.ADRESSEN_RECHNUNGSADRESSEREGION]: "konfigurierbareListenansichtenSpalteTyp.adressen.rechnungsadresseRegion",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.ADRESSEN_RECHNUNGSADRESSESTRASSE]: "konfigurierbareListenansichtenSpalteTyp.adressen.rechnungsadresseStrasse",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.ADRESSEN_RECHNUNGSADRESSETELEFON]: "konfigurierbareListenansichtenSpalteTyp.adressen.rechnungsadresseTelefon",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.ADRESSEN_RECHNUNGSADRESSETITEL]: "konfigurierbareListenansichtenSpalteTyp.adressen.rechnungsadresseTitel",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.ADRESSEN_RECHNUNGSADRESSEVORNAME]: "konfigurierbareListenansichtenSpalteTyp.adressen.rechnungsadresseVorname",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.ADRESSEN_MANDANTENKONTOEMAIL]: "konfigurierbareListenansichtenSpalteTyp.adressen.mandantenKontoEmail",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.ADRESSEN_MANDANTENKONTOMANDANTEINGELADENUTC]: "konfigurierbareListenansichtenSpalteTyp.adressen.mandantenKontoMandantEingeladenUtc",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.ADRESSEN_ZUSATZFELD]: "konfigurierbareListenansichtenSpalteTyp.adressen.zusatzfeld",

    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.DOZIERENDE_MATCHCODE]: "konfigurierbareListenansichtenSpalteTyp.dozierende.matchcode",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.DOZIERENDE_TITEL]: "konfigurierbareListenansichtenSpalteTyp.dozierende.titel",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.DOZIERENDE_BERUF]: "konfigurierbareListenansichtenSpalteTyp.dozierende.beruf",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.DOZIERENDE_VORNAME]: "konfigurierbareListenansichtenSpalteTyp.dozierende.vorname",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.DOZIERENDE_NACHNAME]: "konfigurierbareListenansichtenSpalteTyp.dozierende.nachname",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.DOZIERENDE_GEBURTSDATUM]: "konfigurierbareListenansichtenSpalteTyp.dozierende.geburtsdatum",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.DOZIERENDE_STRASSE]: "konfigurierbareListenansichtenSpalteTyp.dozierende.strasse",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.DOZIERENDE_POSTLEITZAHL]: "konfigurierbareListenansichtenSpalteTyp.dozierende.postleitzahl",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.DOZIERENDE_ORT]: "konfigurierbareListenansichtenSpalteTyp.dozierende.ort",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.DOZIERENDE_REGION]: "konfigurierbareListenansichtenSpalteTyp.dozierende.region",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.DOZIERENDE_LAND]: "konfigurierbareListenansichtenSpalteTyp.dozierende.land",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.DOZIERENDE_EMAIL]: "konfigurierbareListenansichtenSpalteTyp.dozierende.eMail",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.DOZIERENDE_TELEFON]: "konfigurierbareListenansichtenSpalteTyp.dozierende.telefon",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.DOZIERENDE_FAX]: "konfigurierbareListenansichtenSpalteTyp.dozierende.fax",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.DOZIERENDE_MOBIL]: "konfigurierbareListenansichtenSpalteTyp.dozierende.mobil",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.DOZIERENDE_HOMEPAGE]: "konfigurierbareListenansichtenSpalteTyp.dozierende.homepage",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.DOZIERENDE_BEMERKUNGEN]: "konfigurierbareListenansichtenSpalteTyp.dozierende.bemerkungen",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.DOZIERENDE_QUALIFIKATIONEN]: "konfigurierbareListenansichtenSpalteTyp.dozierende.qualifikationen",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.DOZIERENDE_VITA]: "konfigurierbareListenansichtenSpalteTyp.dozierende.vita",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.DOZIERENDE_MANDANTEN_KONTOEMAIL]: "konfigurierbareListenansichtenSpalteTyp.dozierende.kontoEMail",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.DOZIERENDE_MANDANTEN_KONTOMANDANTEINGELADENUTC]: "konfigurierbareListenansichtenSpalteTyp.dozierende.kontoEingeladenAm",
    [KONFIGURIERBARE_LISTENANSICHT_SPALTE_TYP.DOZIERENDE_ZUSATZFELD]: "konfigurierbareListenansichtenSpalteTyp.dozierende.zusatzfeld"
}

export const konfigurierbareListenansichtTranslationKeyMap = {
    [KONFIGURIERBARE_LISTENANSICHT_TYP.SEMINARE]: "konfigurierbareListenansichtenTyp.seminare",
    [KONFIGURIERBARE_LISTENANSICHT_TYP.TEILNAHMEN]: "konfigurierbareListenansichtenTyp.teilnahmen",
    [KONFIGURIERBARE_LISTENANSICHT_TYP.ZAHLUNGEN]: "konfigurierbareListenansichtenTyp.zahlungen",
    [KONFIGURIERBARE_LISTENANSICHT_TYP.ADRESSEN]: "konfigurierbareListenansichtenTyp.adressen",
    [KONFIGURIERBARE_LISTENANSICHT_TYP.DOZIERENDE]: "konfigurierbareListenansichtenTyp.dozierende",
}

export const sortierrichtungTranslationKeyMap = {
    [SORTIERRICHTUNG.ABSTEIGEND]: "sortierrichtung.absteigend",
    [SORTIERRICHTUNG.AUFSTEIGEND]: "sortierrichtung.aufsteigend",
}